import { useState, useEffect, useCallback } from "react";
import { getSupabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useDealerLog, LOG_ACTIONS } from "@/hooks/useDealerLog";

interface DealerUser {
  id: string;
  username: string;
  full_name: string;
  role: string;
  is_active: boolean;
  created_at: string;
}

export default function DealerAdminUsersPage() {
  const { user } = useAuth();
  const { logAction } = useDealerLog();
  const dealerId = user?.dealerId;

  const [users, setUsers] = useState<DealerUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ username: "", fullName: "", role: "", password: "" });

  const fetchData = useCallback(async () => {
    if (!dealerId) return;
    setLoading(true);
    setError("");
    try {
      const supabase = getSupabase();
      const { data, error: err } = await supabase
        .from("dealer_users")
        .select("id, username, full_name, role, is_active, created_at")
        .eq("dealer_id", dealerId)
        .order("created_at", { ascending: false });

      if (err) throw err;
      setUsers(data || []);
    } catch (e: any) {
      setError(e.message || "Kullanıcılar yüklenirken hata oluştu.");
    } finally {
      setLoading(false);
    }
  }, [dealerId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const addUser = async () => {
    if (!dealerId || !form.username.trim() || !form.fullName.trim()) return;

    setSaving(true);
    try {
      const supabase = getSupabase();
      const { error: err } = await supabase.from("dealer_users").insert({
        dealer_id: dealerId,
        username: form.username.trim(),
        password_hash: form.password || "123456",
        full_name: form.fullName.trim(),
        role: form.role || "Satış Temsilcisi",
        is_active: true,
      });

      if (err) throw err;

      setForm({ username: "", fullName: "", role: "", password: "" });
      setShowModal(false);
      logAction(dealerId, user?.id || null, LOG_ACTIONS.USER_ADD, `"${form.fullName.trim()}" kullanıcısı eklendi`);
      await fetchData();
    } catch (e: any) {
      alert(e.message || "Kullanıcı eklenirken hata oluştu.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (u: DealerUser) => {
    try {
      const supabase = getSupabase();
      const { error: err } = await supabase
        .from("dealer_users")
        .update({ is_active: !u.is_active })
        .eq("id", u.id);

      if (err) throw err;
      logAction(dealerId, user?.id || null, LOG_ACTIONS.USER_UPDATE, `"${u.full_name}" kullanıcısı ${u.is_active ? "pasif" : "aktif"} yapıldı`);
      await fetchData();
    } catch (e: any) {
      alert(e.message || "Durum güncellenirken hata oluştu.");
    }
  };

  const deleteUser = async (u: DealerUser) => {
    if (!confirm(`${u.full_name} kullanıcısını silmek istediğinize emin misiniz?`)) return;

    try {
      const supabase = getSupabase();
      const { error: err } = await supabase.from("dealer_users").delete().eq("id", u.id);
      if (err) throw err;
      logAction(dealerId, user?.id || null, LOG_ACTIONS.USER_DELETE, `"${u.full_name}" kullanıcısı silindi`);
      await fetchData();
    } catch (e: any) {
      alert(e.message || "Kullanıcı silinirken hata oluştu.");
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h4 className="text-lg font-semibold text-foreground-950"><a id="dealer-admin-users">Bayi Kullanıcıları</a></h4>
          <p className="text-sm text-foreground-500 mt-0.5">Sadece kendi bayinize kullanıcı ekleyin ve yönetin</p>
        </div>
        <button onClick={() => setShowModal(true)} className="flex items-center gap-2 px-4 py-2 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-add-line"></i> Kullanıcı Ekle
        </button>
      </div>

      {error && (
        <div className="bg-accent-100 border border-accent-300 text-accent-900 rounded-xl px-4 py-3 mb-4 text-sm flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {error}
          <button onClick={fetchData} className="ml-auto text-accent-700 font-medium cursor-pointer">Tekrar Dene</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
        </div>
      ) : (
        <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[500px]">
              <thead>
                <tr className="bg-background-100 text-left">
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">Kullanıcı Adı</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">Ad Soyad</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">Rol</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">Durum</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-center">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center text-foreground-400 text-sm">
                      <i className="ri-user-line text-3xl block mb-2"></i>
                      Henüz bayi kullanıcısı eklenmemiş.
                    </td>
                  </tr>
                ) : (
                  users.map((u) => (
                    <tr key={u.id} className="border-t border-background-200/70 hover:bg-background-100/50 transition-colors">
                      <td className="px-4 py-3 font-medium text-foreground-900">{u.username}</td>
                      <td className="px-4 py-3 text-foreground-900">{u.full_name}</td>
                      <td className="px-4 py-3 text-foreground-600">{u.role}</td>
                      <td className="px-4 py-3">
                        <button onClick={() => toggleActive(u)} className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer ${u.is_active ? "bg-primary-100 text-primary-700" : "bg-foreground-100 text-foreground-500"}`}>
                          {u.is_active ? "Aktif" : "Pasif"}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <button className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-primary-600 hover:bg-primary-100 cursor-pointer"><i className="ri-edit-line text-sm"></i></button>
                          <button onClick={() => deleteUser(u)} className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-accent-500 hover:bg-accent-100 cursor-pointer"><i className="ri-delete-bin-line text-sm"></i></button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShowModal(false)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-base font-semibold text-foreground-950"><a id="add-dealer-user">Yeni Bayi Kullanıcısı</a></h4>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer"><i className="ri-close-line"></i></button>
            </div>
            <div className="space-y-3">
              <div><label className="block text-xs font-medium text-foreground-500 mb-1">Kullanıcı Adı *</label><input type="text" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-accent-400" /></div>
              <div><label className="block text-xs font-medium text-foreground-500 mb-1">Ad Soyad *</label><input type="text" value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-accent-400" /></div>
              <div><label className="block text-xs font-medium text-foreground-500 mb-1">Rol</label><input type="text" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-accent-400" placeholder="Satış Temsilcisi" /></div>
              <div><label className="block text-xs font-medium text-foreground-500 mb-1">Şifre</label><input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-accent-400" placeholder="Varsayılan: 123456" /></div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setShowModal(false)} className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap">İptal</button>
              <button onClick={addUser} disabled={saving} className="flex-1 py-2.5 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60">
                {saving ? "Kaydediliyor..." : "Kaydet"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}